import { useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LogOut,
  LayoutDashboard,
  UserCog,
  HandHeart,
  Megaphone,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getAvatarSrc } from "../lib/avatar";
import BrandLogo from "./BrandLogo";

const links = [
  { to: "/member", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/activities/feed", label: "Activities", icon: Megaphone },
  { to: "/member/profile", label: "Profile", icon: UserCog },
  { to: "/member/welfare", label: "Welfare fund", icon: HandHeart },
];

export default function MemberLayout({ children }) {
  const { member, memberLogout } = useAuth();
  const nav = useNavigate();
  const timerRef = useRef(null);

  // AUTO LOGOUT AFTER 10 MIN INACTIVITY
  useEffect(() => {
    const AUTO_LOGOUT_TIME = 10 * 60 * 1000; // 10 min

    const doLogout = async () => {
      try {
        await memberLogout();
      } catch (e) {}

      localStorage.removeItem("ospf_member_token");
      sessionStorage.clear();

      nav("/member/login", { replace: true });
    };

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() => {
        doLogout();
      }, AUTO_LOGOUT_TIME);
    };

    const events = [
      "mousemove",
      "mousedown",
      "click",
      "scroll",
      "keypress",
      "touchstart",
    ];

    events.forEach((event) => window.addEventListener(event, resetTimer));

    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);

      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [memberLogout, nav]);

  const handleLogout = async () => {
    try {
      await memberLogout();
    } catch (e) {}

    localStorage.removeItem("ospf_member_token");
    sessionStorage.clear();

    nav("/member/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-cream-deep/30">
      <header className="bg-white border-b border-black/5 sticky top-0 z-40">
        <div className="container-px py-4 flex items-center justify-between gap-6">
          {/* LEFT */}
          <div className="flex items-center gap-3">
            <BrandLogo compact />

            <div>
              <div className="overline">Member console</div>

              <div className="font-heading text-base font-semibold text-ink">
                {member?.name || member?.full_name || "Member"}
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="flex items-center gap-3">
            {getAvatarSrc(member) ? (
              <img
                src={getAvatarSrc(member)}
                alt="avatar"
                className="w-9 h-9 rounded-full object-contain bg-white border border-black/10"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-brand/10 text-brand flex items-center justify-center text-sm font-medium">
                {initials(member?.name || member?.full_name)}
              </div>
            )}

            <button
              onClick={handleLogout}
              className="btn-ghost flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>

        {/* NAV */}
        <nav className="container-px flex gap-1 border-t border-black/5 overflow-x-auto">
          {links.map((l) => {
            const Icon = l.icon;

            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                    isActive
                      ? "border-brand text-brand"
                      : "border-transparent text-ink-soft hover:text-ink"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                {l.label}
              </NavLink>
            );
          })}
        </nav>
      </header>

      <main className="container-px py-10">{children}</main>
    </div>
  );
}

function initials(name = "") {
  return name
    .split(" ")
    .slice(0, 2)
    .map((p) => p?.[0] || "")
    .join("")
    .toUpperCase();
}
